/* ======================================================================
   CART PANEL — Shopify live-cart edition
   · Reads #pallet-cart-data (rendered by snippets/pallet-cart-data.liquid)
   · Re-syncs whenever the theme changes the cart (qty +/- , remove)
   · Colours + spec line on the theme's own cart rows
   Depends on: pallet-packer.js, freight.js, pallet-scene.js (loaded first)

   ⚠️  THIS FILE DOES NOT OWN THE FREIGHT PRICE.

   #tFreight / #tTotal / #tPallets / #tDest belong to the ShipDuo snippet
   (snippets/shipduo-cart-rates.liquid), which fills them from a live
   multi-carrier quote and CLEARS them on any cart change until the shopper
   presses "Estimate shipping" again.

   render() used to write all four from freight.js's `m.freight`. That number
   comes from the BREAKS weight table at the top of freight.js, which its own
   header calls a prototype placeholder — so on every quantity change the
   theme painted an invented price over a real one, a frame after ShipDuo had
   deliberately cleared it. Two writers on one element is a race, and the
   invented number was winning.

   What this file still owns: the panel's own stats (#sPallets, #sCases,
   #sWeight, #sUtil), the under-filled pallet notice, the per-pallet list, the
   3D scene and the cart row decoration. Those are pallet-plan facts, not
   prices. The product legend under the canvas is gone — the per-pallet list
   already names every product with its colour.

   Cart row spec line unit: the "Single" variant prints lb/case and $/case
   (or /pack when the product's other variant is sold in Packs); every other
   variant prints /pallet. The unit comes from "unit" in
   snippets/pallet-cart-data.liquid — see lineUnit().
   ====================================================================== */

var PALETTE = ['#e8912d','#28c76f','#4f8cff','#e754c9','#f6c445','#9b6bff','#2ed3c6','#ff6b6b','#7f8c8d','#f97316'];
var ROOT    = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

var items = [];                 /* packer input — freight.js reads this global */
var CURRENCY = 'USD';
var appliedList = [];           /* kept for compatibility — suggestions UI is off */
var flashSkus = [];
var lastList = [];
var recClosed = false;
var sceneReady = false;
var busy = false;

/* A pallet filled to this percentage or less, on a plan of two or more
   pallets, is called out under the stats: the shopper is paying a whole
   pallet's freight for a handful of cases. See renderUnderfilledPallet(). */
var UNDERFILLED_PALLET_PCT = 10;

/* currency-aware money (overrides the USD-only helper in freight.js) */
usd = function(n){
  try { return n.toLocaleString('en-US', {style:'currency', currency:CURRENCY}); }
  catch(e){ return n.toFixed(2); }
};

var WARN_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 32 32" aria-hidden="true">'
  + '<path style="stroke:none;fill-rule:nonzero;fill:#BD6B1B;fill-opacity:1" d="M28 14.668c0 7.398-5.121 14.32-12 16-6.879-1.68-12-8.602-12-16v-8l12-5.336 12 5.336v8M16 28c5-1.332 9.332-7.281 9.332-13.04V8.399L16 4.238l-9.332 4.16v6.563C6.668 20.719 11 26.668 16 28M14.668 9.332h2.664v8h-2.664v-8m0 10.668h2.664v2.668h-2.664Zm0 0"/>'
  + '</svg>';

/* =====================================================================
   DATA
   ===================================================================== */
function parseCartData(root){
  var el = (root || document).querySelector('#pallet-cart-data');
  if(!el) return null;
  try { return JSON.parse(el.textContent); } catch(e){ console.warn('[pallet] bad JSON', e); return null; }
}

var ALL_LINES = [];
var RAW_CASES = 0;
var RAW_WEIGHT = 0;

/* "case" / "pack" for the Single variant, "pallet" for the bulk one.
   Comes from "unit" in snippets/pallet-cart-data.liquid; falls back to the
   variant title when the snippet hasn't been updated yet. */
function lineUnit(ln){
  if(ln.unit) return String(ln.unit).toLowerCase();
  var t = String(ln.variant_title || '').toLowerCase();
  if(t.indexOf('single') > -1) return 'case';
  return 'pallet';
}

function buildItems(data){
  if(!data) return;
  CURRENCY = data.currency || 'USD';
  RAW_CASES = (data.lines || []).reduce(function(a, ln){ return a + (+ln.qty || 0); }, 0);
  RAW_WEIGHT = (data.lines || []).reduce(function(a, ln){ return a + (+ln.wt || 0) * (+ln.qty || 0); }, 0);
  var colorIdx = 0;
  items = []; ALL_LINES = [];
  (data.lines || []).forEach(function(ln){
    var color = ln.color || PALETTE[colorIdx++ % PALETTE.length];
    var L = +ln.L || 0, W = +ln.W || 0, H = +ln.H || 0, wt = +ln.wt || 0;

    ALL_LINES.push({ id: ln.id, name: ln.name, qty: +ln.qty || 0, color: color, sku: ln.sku || '',
      L: L, W: W, H: H, wt: wt, price: +ln.price || 0, unit: lineUnit(ln) });

    /* dimensions are required — weight is optional */
    if(!L || !W || !H){
      console.warn('[pallet] skipped "' + ln.name + '" — missing dimension metafield' +
        (ln.dim_raw ? ' (raw: "' + ln.dim_raw + '")' : ''));
      return;
    }
    if(!wt){
      console.warn('[pallet] "' + ln.name + '" has no weight — pallet weight limit ignored for it');
    }

    items.push({ sku:String(ln.id), id:ln.id, key:ln.key, skuLabel:ln.sku||'', name:ln.name,
      L:L, W:W, H:H, wt:wt, price:+ln.price||0, qty:+ln.qty||0, color:color });
  });
}

function cartLines(list){
  return (list||items).filter(function(i){ return i.qty>0 }).map(function(i){
    return { sku:i.sku, name:i.name, qty:i.qty, case:{l:i.L,w:i.W,h:i.H},
             weight:i.wt, stackable:true, maxPerPallet:null, color:i.color };
  });
}

function dimOf(i){ return i.L+'×'+i.W+'×'+i.H+'"' }

/* Items larger than the pallet itself (e.g. a product sold as a full pallet)
   can't be packed — they ship as their own pallet, one per case. */
function fitsPallet(i){
  var R = window.RULES || {};
  var PW = R.W || 40, PL = R.L || 48, PH = R.maxH || 72;
  var footprint = (i.L <= PL && i.W <= PW) || (i.L <= PW && i.W <= PL);
  return footprint && i.H <= PH;
}
function splitItems(list){
  var packable = [], oversized = [];
  (list || items).forEach(function(i){ (fitsPallet(i) ? packable : oversized).push(i); });
  return { packable: packable, oversized: oversized };
}

var PLAN = { sig: null, units: [], lines: [], summary: null, utilization: null, handlingUnit: 'Pallet', shipmentType: null };
var planInFlight = null;
var planFailedSig = null;

function planConfig(){ return window.ShipDuoCartConfig || {}; }

function planConfigured(){
  var cfg = planConfig();
  return !!(cfg.planEndpoint && cfg.apiKey);
}

function cartSignature(){
  return ALL_LINES.map(function(l){ return l.id + ':' + l.qty; }).join(',');
}

function hasPlan(){ return PLAN.sig !== null && PLAN.sig === cartSignature(); }

function planPending(){
  return planConfigured()
    && ALL_LINES.length > 0
    && !hasPlan()
    && planFailedSig !== cartSignature();
}

function setPlanLoading(on){
  var el = document.getElementById('planLoading');
  if(el) el.classList.toggle('is-active', !!on);
}

function requestPlan(){
  var cfg = planConfig();
  var sig = cartSignature();

  if(!cfg.planEndpoint || !cfg.apiKey || !ALL_LINES.length){ return; }
  if(sig === PLAN.sig || sig === planInFlight){ return; }
  if(planFailedSig === sig) planFailedSig = null;
  planInFlight = sig;

  fetch(cfg.planEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': 'Bearer ' + cfg.apiKey
    },
    body: JSON.stringify({
      items: ALL_LINES.filter(function(l){ return l.qty > 0; })
        .map(function(l){ return { variant_id: l.id, quantity: l.qty }; })
    })
  })
  .then(function(r){ return r.json(); })
  .then(function(json){
    if(planInFlight !== sig) return;
    planInFlight = null;

    var data = json && json.data;
    if(!json || json.status !== true || !data || !data.available){
      console.warn('[pallet] plan unavailable', json && json.message);
      planFailedSig = sig;
      render(false);
      return;
    }

    PLAN = {
      sig: sig,
      units: data.units || [],
      lines: data.lines || [],
      summary: data.plan || null,
      utilization: data.utilization || null,
      handlingUnit: data.handling_unit || 'Pallet',
      shipmentType: data.shipment_type || null
    };
    applyPlanToLines(PLAN.lines);
    render(false);
  })
  .catch(function(e){
    if(planInFlight === sig) planInFlight = null;
    console.warn('[pallet] plan request failed', e);
    planFailedSig = sig;
    render(false);
  });
}

function applyPlanToLines(lines){
  var byId = {};
  (lines || []).forEach(function(line){ byId[String(line.variant_id)] = line; });

  function apply(entry){
    var line = byId[String(entry.id)];
    if(!line) return;
    if(line.color)     entry.color = line.color;
    if(line.length)    entry.L  = line.length;
    if(line.width)     entry.W  = line.width;
    if(line.height)    entry.H  = line.height;
    if(line.weight_lb) entry.wt = line.weight_lb;
  }

  ALL_LINES.forEach(apply);
  items.forEach(apply);
}

function metricsFromPlan(){
  var units = PLAN.units || [];
  var weight = 0;

  units.forEach(function(u){ weight += (+u.weight_lb || 0); });

  var summary = PLAN.summary || {};
  var util = PLAN.utilization || {};
  var hasUtil = typeof util.utilization_percent === 'number';

  return {
    pallets: units.length,
    cases: RAW_CASES,
    weight: summary.total_weight_lb || weight,
    util: hasUtil ? Math.max(0, Math.min(1, util.utilization_percent / 100)) : 0,
    emptyPct: typeof util.empty_percent === 'number' ? util.empty_percent : null,
    res: { cfg: sceneConfig(units), pallets: units.map(planPallet) },
    fromPlan: true
  };
}

function sceneConfig(units){
  var W = 40, L = 48, maxH = 72, deck = 5.5;

  units.forEach(function(u){
    W = Math.max(W, +u.width || 0);
    L = Math.max(L, +u.length || 0);
    maxH = Math.max(maxH, (+u.content_height || +u.height || 0));
    deck = Math.max(deck, +u.deck_height || 0);
  });

  return { W: W, L: L, maxH: maxH, deck: deck };
}

function planPallet(unit){
  var boxes = placeUnit(unit);

  return {
    cases: boxes.length,
    boxes: boxes,
    cfg: {
      W: +unit.width || 40,
      L: +unit.length || 48,
      deck: +unit.deck_height || 0,
      maxH: +unit.height_limit || +unit.content_height || +unit.height || 0
    }
  };
}

function serverLayers(unit){
  var layers = unit.layers;
  if(!layers || !layers.length) return null;

  var byId = {};
  (unit.contents || []).forEach(function(c){ byId[String(c.variant_id)] = c; });

  var boxes = [];

  layers.forEach(function(layer){
    (layer.boxes || []).forEach(function(b){
      var w = +b.w || 0, l = +b.l || 0, h = +b.h || 0;
      if(w <= 0 || l <= 0 || h <= 0) return;

      var c = byId[String(b.id)] || {};
      boxes.push({ x:+b.x || 0, y:+b.y || 0, z:+b.z || 0, w:w, l:l, h:h,
                   color:c.color, name:c.name });
    });
  });

  return boxes.length ? boxes : null;
}

function placeUnit(unit){
  var served = serverLayers(unit);
  if(served) return served;

  var deckW = +unit.width || 0, deckL = +unit.length || 0;
  var cases = [];

  (unit.contents || []).forEach(function(c){
    var qty = Math.max(0, +c.quantity || 0);
    for(var i = 0; i < qty; i++){
      cases.push({ w: +c.width || 0, l: +c.length || 0, h: +c.height || 0,
                   color: c.color, name: c.name });
    }
  });

  cases = cases.filter(function(c){ return c.w > 0 && c.l > 0 && c.h > 0; });

  if(!cases.length || !deckW || !deckL){
    var h = +unit.content_height || +unit.height || 0;
    return h > 0 ? [{ x:0, y:0, z:0, w:deckW || 40, l:deckL || 48, h:h, color:'#d9b382' }] : [];
  }

  cases.forEach(function(c){
    var a = { w:c.w, l:c.l }, b = { w:c.l, l:c.w };
    var aFits = a.w <= deckW && a.l <= deckL, bFits = b.w <= deckW && b.l <= deckL;
    var pick = a;
    if(!aFits && bFits) pick = b;
    else if(aFits && bFits){
      var na = Math.floor(deckW/a.w) * Math.floor(deckL/a.l);
      var nb = Math.floor(deckW/b.w) * Math.floor(deckL/b.l);
      if(nb > na) pick = b;
    }
    c.fw = pick.w; c.fl = pick.l;
  });

  var E = 1e-6, boxes = [];
  var z = 0, y = 0, layerHeight = 0, i = 0;

  while(i < cases.length){
    var head = cases[i];
    var key = head.fw + 'x' + head.fl + 'x' + head.h;
    var run = 0;

    while(i + run < cases.length){
      var next = cases[i + run];
      if(next.fw + 'x' + next.fl + 'x' + next.h !== key) break;
      run++;
    }

    var perRow = Math.max(1, Math.floor((deckW + E) / head.fw));
    var rowsNeeded = Math.ceil(run / perRow);

    if(z > 0 && z + rowsNeeded * head.fl > deckL + E){
      y += layerHeight; layerHeight = 0; z = 0;
    }

    var placed = 0;

    while(placed < run){
      if(z > 0 && z + head.fl > deckL + E){
        y += layerHeight; layerHeight = 0; z = 0;
      }

      var take = Math.min(perRow, run - placed);

      for(var k = 0; k < take; k++){
        var c = cases[i + placed + k];
        boxes.push({ x:k * c.fw, y:y, z:z, w:c.fw, l:c.fl, h:c.h, color:c.color, name:c.name });
      }

      z += head.fl;
      layerHeight = Math.max(layerHeight, head.h);
      placed += take;
    }

    i += run;
  }

  var minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  boxes.forEach(function(b){
    minX = Math.min(minX, b.x); maxX = Math.max(maxX, b.x + b.w);
    minZ = Math.min(minZ, b.z); maxZ = Math.max(maxZ, b.z + b.l);
  });
  var shiftX = (deckW - (maxX - minX))/2 - minX, shiftZ = (deckL - (maxZ - minZ))/2 - minZ;
  boxes.forEach(function(b){ b.x += shiftX; b.z += shiftZ; });

  return boxes;
}

/* run the packer on what fits, then add one full pallet per oversized case */
function computeMetrics(){
  var sp = splitItems(items);
  var m;
  try { m = metrics(sp.packable); }
  catch(e){
    console.warn('[pallet] metrics failed', e);
    m = { pallets:0, cases:0, weight:0, util:0, freight:0, subtotal:0, res:null };
  }
  /* drop the packer's placeholder empty pallet so the scene never shows a bare deck */
  if(m.res && m.res.pallets){
    m.res.pallets = m.res.pallets.filter(function(p){ return p.boxes && p.boxes.length; });
  }

  var packedPallets = m.pallets || 0;
  var extra = sp.oversized.reduce(function(a,i){ return a + (i.qty>0 ? i.qty : 0); }, 0);
  if(!extra) return m;

  var total = packedPallets + extra;

  m.util     = ((m.util || 0) * packedPallets + 1.0 * extra) / total;
  m.pallets  = total;
  m.weight   = (m.weight || 0) + sp.oversized.reduce(function(a,i){ return a + i.wt * i.qty; }, 0);
  m.freight  = m.weight ? m.weight * rateFor(m.weight) : 0;
  m.subtotal = (m.subtotal || 0) + sp.oversized.reduce(function(a,i){ return a + i.price * i.qty; }, 0);

  /* pseudo pallets for the 3D scene: one solid block per case */
  var R = window.RULES || {};
  if(m.res && m.res.pallets && m.res.cfg){
    var cfg = m.res.cfg;
    sp.oversized.forEach(function(i){
      for(var k=0; k<i.qty; k++){
        m.res.pallets.push({
          cases: 1, oversized: true,
          boxes: [{ x:0, y:0, z:0, w:cfg.W, l:cfg.L, h:Math.min(i.H, cfg.maxH), color:i.color, sku:i.sku }]
        });
      }
    });
  } else if(R.W && R.L){
    var pallets = [];
    sp.oversized.forEach(function(i){
      for(var k=0; k<i.qty; k++){
        pallets.push({ cases:1, oversized:true,
          boxes:[{ x:0, y:0, z:0, w:R.W, l:R.L, h:Math.min(i.H, R.maxH || i.H), color:i.color, sku:i.sku }] });
      }
    });
    m.res = { cfg: R, pallets: pallets };
  }
  return m;
}

/* =====================================================================
   RENDER
   ===================================================================== */
function setText(id, v){ var el=document.getElementById(id); if(el) el.textContent = v; }
function setHtml(id, v){ var el=document.getElementById(id); if(el) el.innerHTML = v; }

/* colour bar + spec line on the theme's own cart rows */
function decorateRows(){
  ALL_LINES.forEach(function(ln){
    var row = document.querySelector('.js-cart-item[data-variant-id="' + ln.id + '"]');
    if(!row) return;
    row.style.setProperty('--line-color', ln.color);
    row.classList.add('has-pallet-color');

    var details = row.querySelector('.cart-item-details');
    if(!details) return;
    var meta = details.querySelector('.cart-item-meta');
    if(!meta){
      meta = document.createElement('p');
      meta.className = 'cart-item-meta';
      var name = details.querySelector('.cart-item-name');
      if(name && name.nextSibling) details.insertBefore(meta, name.nextSibling);
      else details.appendChild(meta);
    }
    /* Single variant → /case or /pack, bulk variant → /pallet */
    var unit = ln.unit || 'pallet';
    var parts = [];
    if(ln.sku) parts.push(ln.sku);
    if(ln.L && ln.W && ln.H) parts.push(ln.L + '×' + ln.W + '×' + ln.H + '"');
    if(ln.wt) parts.push(num(Math.round(ln.wt)) + ' lb/' + unit);
    if(ln.price) parts.push(usd(ln.price) + '/' + unit);
    var txt = parts.join(' · ');
    if(meta.textContent !== txt) meta.textContent = txt;   /* only touch DOM when changed */
  });
}

/* Per-unit fill, 0–100, from the plan API's own figure (content volume over
   the pallet's capacity — see the backend's `utilization_percent`). Falls
   back to the two volumes when the percentage is missing; null when neither
   is usable, and such a unit is never called out. */
function unitFillPct(u){
  if(typeof u.utilization_percent === 'number') return u.utilization_percent;
  var cap = +u.capacity_volume_cuin || 0, content = +u.content_volume_cuin || 0;
  return cap > 0 ? (content / cap) * 100 : null;
}

function caseWord(n){ return n === 1 ? 'case' : 'cases'; }

/* Replaces the old "You're paying to ship N% empty space" banner. That line
   fired on almost every cart (a solidly packed short stack still reads as
   mostly air against the pallet's full stack limit) and told the shopper
   nothing they could act on.

   This one fires only when the plan needs TWO OR MORE pallets and one of them
   is at most UNDERFILLED_PALLET_PCT full — a pallet the shopper is paying for
   to carry a handful of cases. It names those cases and says that dropping
   them ships the order on one pallet fewer. When every pallet is reasonably
   full, or the order fits on one pallet, nothing is shown. Only a server plan
   can be judged this way: the local fallback packer knows nothing about the
   merchant's pallet capacity, so it hides the notice. */
function renderUnderfilledPallet(){
  var el = document.getElementById('waste');
  if(!el) return;

  var units = hasPlan() ? (PLAN.units || []) : [];
  var label = PLAN.handlingUnit || 'Pallet';

  if(units.length < 2 || label === 'Box'){
    el.style.display = 'none';
    return;
  }

  var worst = null, worstIdx = -1, worstPct = Infinity;
  units.forEach(function(u, i){
    /* A product that ships as its own pallet is full by definition. */
    if(u.as_is) return;
    var pct = unitFillPct(u);
    if(pct === null || pct > UNDERFILLED_PALLET_PCT) return;
    if(pct < worstPct){ worst = u; worstIdx = i; worstPct = pct; }
  });

  if(!worst){
    el.style.display = 'none';
    return;
  }

  var contents = (worst.contents || []).filter(function(c){ return (+c.quantity || 0) > 0; });
  var totalCases = contents.reduce(function(a, c){ return a + (+c.quantity || 0); }, 0);
  var shown = Math.max(1, Math.round(worstPct));
  var fewer = units.length - 1;
  var name = esc(label) + ' ' + (worstIdx + 1);

  var carries = contents.map(function(c){
    return '<b>' + (+c.quantity || 0) + ' ' + caseWord(+c.quantity || 0) + '</b> of ' + esc(c.name || 'this item');
  });
  var carriesText = carries.length
    ? carries.slice(0, -1).join(', ') + (carries.length > 1 ? ' and ' : '') + carries[carries.length - 1]
    : '';

  var msg = name + ' is only <b>' + shown + '% full</b>';
  if(carriesText){
    msg += ' — it carries just ' + carriesText + '. Remove '
      + (totalCases === 1 ? 'that case' : 'those ' + totalCases + ' cases')
      + ' and this order ships on <b>' + fewer + ' ' + esc(fewer === 1 ? label : label + 's').toLowerCase()
      + '</b> instead of ' + units.length + ', so you\'ll pay less freight.';
  } else {
    msg += '. Remove what\'s on it and this order ships on <b>' + fewer + ' '
      + esc(fewer === 1 ? label : label + 's').toLowerCase()
      + '</b> instead of ' + units.length + ', so you\'ll pay less freight.';
  }

  el.style.display = '';
  el.innerHTML = '<span style="display:inline-flex;flex:0 0 17px">' + WARN_SVG + '</span>'
    + '<span>' + msg + '</span>';
}

function esc(s){
  return String(s == null ? '' : s).replace(/[&<>"]/g, function(c){
    return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c];
  });
}

function unitDims(u){
  if(u.dimensions) return u.dimensions;
  var L = +u.length || 0, W = +u.width || 0, H = +u.height || 0;
  return (L && W && H) ? (L + ' x ' + W + ' x ' + H + ' in') : '';
}

function renderUnits(){
  var host = document.getElementById('palletUnits');
  if(!host) return;

  var units = hasPlan() ? (PLAN.units || []) : [];

  if(!units.length){
    host.innerHTML = '';
    host.style.display = 'none';
    return;
  }

  var label = (PLAN.handlingUnit || 'Pallet');

  var rows = units.map(function(u, i){
    var bits = [];
    var dims = unitDims(u);
    if(dims) bits.push(dims);
    if(+u.weight_lb) bits.push(num(Math.round(+u.weight_lb)) + ' lb');

    var inside = (u.contents || []).filter(function(c){ return (+c.quantity || 0) > 0; })
      .map(function(c){
        return '<span style="display:inline-flex;align-items:center;gap:5px">'
          + '<i style="width:9px;height:9px;border-radius:2px;flex:0 0 9px;background:' + esc(c.color || '#bbb') + '"></i>'
          + esc(c.name || 'Item') + ' × ' + (+c.quantity || 0) + '</span>';
      }).join('');

    return '<li style="padding:7px 0;border-top:1px solid rgba(0,0,0,.07)">'
      + '<div style="display:flex;justify-content:space-between;gap:10px;font-weight:600">'
      + '<span>' + esc(label) + ' ' + (i + 1) + (u.name ? ' · ' + esc(u.name) : '') + '</span>'
      + '<span style="font-weight:500;opacity:.75">' + esc(bits.join(' · ')) + '</span>'
      + '</div>'
      + (inside ? '<div style="display:flex;flex-wrap:wrap;gap:4px 14px;margin-top:4px;opacity:.75">' + inside + '</div>' : '')
      + '</li>';
  }).join('');

  host.style.display = '';
  host.innerHTML = '<div style="margin-top:4px;font-size:12.5px;line-height:1.35">'
    + '<div style="font-weight:600;opacity:.6;letter-spacing:.04em;text-transform:uppercase;font-size:11px">'
    + esc(label) + ' sizes</div>'
    + '<ul style="list-style:none;margin:4px 0 0;padding:0">' + rows + '</ul>'
    + '</div>';
}

function render(pulse){
  var pending = planPending();
  var m = pending ? null : (hasPlan() ? metricsFromPlan() : computeMetrics());

  /* #tFreight / #tTotal / #tPallets / #tDest are NOT written here — the
     ShipDuo snippet owns them. See the header of this file. */

  setPlanLoading(pending);

  setText('kPallets', (!pending && hasPlan() && PLAN.handlingUnit === 'Box') ? 'Boxes' : 'Pallets');
  setText('sPallets', pending ? '—' : m.pallets);
  setText('sCases', RAW_CASES);
  setHtml('sWeight', pending ? '—' : num(Math.round(m.fromPlan ? m.weight : RAW_WEIGHT)) + '<small>lb</small>');
  setHtml('sUtil',   pending ? '—' : Math.round(m.util*100) + '<small>%</small>');

  var list = []; lastList = list;

  if(pending){
    var wasteEl = document.getElementById('waste');
    if(wasteEl) wasteEl.style.display = 'none';
  } else {
    renderUnderfilledPallet();
  }

  /* suggestions UI intentionally disabled */
  var rec = document.getElementById('rec');
  if(rec) rec.innerHTML = '';

  /* no product legend under the canvas — renderUnits() names every product,
     with its colour, on the pallet that carries it */

  decorateRows();
  renderUnits();

  if(!pending && sceneReady && m.res) renderPallets(m.res);

  /* flash the theme's own cart rows for changed variants */
  if(flashSkus.length){
    flashSkus.forEach(function(vid){
      var row = document.querySelector('.js-cart-item[data-variant-id="'+vid+'"]');
      if(row){ row.classList.remove('flash'); void row.offsetWidth; row.classList.add('flash'); }
    });
    flashSkus = [];
  }

  if(!pending && pulse !== false){
    var v = document.getElementById('viz');
    if(v){ v.classList.remove('pulse'); void v.offsetWidth; v.classList.add('pulse'); }
  }
}

/* =====================================================================
   SHOPIFY CART SYNC
   ===================================================================== */
function sectionIds(){
  var a = document.getElementById('main-cart-items');
  var b = document.getElementById('main-cart-footer');
  return { items: a && a.dataset.id, footer: b && b.dataset.id };
}

/* swap the theme's own markup from a fresh section render */
function patchThemeDom(html, which){
  if(!html) return;
  var doc = new DOMParser().parseFromString(html, 'text/html');
  function swap(sel){
    var from = doc.querySelector(sel), to = document.querySelector(sel);
    if(from && to) to.innerHTML = from.innerHTML;
  }
  if(which === 'items'){
    swap('#main-cart-items .js-contents');
    swap('.cart-product-qty');
    var wrap = document.querySelector('cart-items');
    if(wrap){ wrap.classList.toggle('is-empty', !doc.querySelector('.js-cart-item')); }
  } else {
    swap('#main-cart-footer .js-contents');
    swap('[data-cart-subtotal]');
  }
}

/* Theme changed the cart → re-read metafield JSON from a fresh section render */
var reloadTimer = null;
function reloadFromSection(){
  var ids = sectionIds();
  if(!ids.items) return;
  fetch(location.pathname + '?sections=' + ids.items, {headers:{'X-Pallet-Panel':'1'}})
    .then(function(r){ return r.json(); })
    .then(function(json){
      var html = json[ids.items];
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var data = parseCartData(doc);
      if(data){ appliedList = []; buildItems(data); requestPlan(); render(true); }
    })
    .catch(function(e){ console.warn('[pallet] reload failed', e); });
}
function scheduleReload(delay){
  clearTimeout(reloadTimer);
  reloadTimer = setTimeout(reloadFromSection, delay || 150);
}

/* We change the cart → update Shopify, then patch theme rows + our state */
function updateCart(updates, done){
  var ids = sectionIds();
  busy = true; render(false);
  fetch(ROOT + 'cart/update.js', {
    method: 'POST',
    headers: {'Content-Type':'application/json', 'Accept':'application/json', 'X-Pallet-Panel':'1'},
    body: JSON.stringify({ updates: updates, sections: [ids.items, ids.footer].filter(Boolean).join(',') })
  })
  .then(function(r){ if(!r.ok) throw new Error(r.status); return r.json(); })
  .then(function(cart){
    var s = cart.sections || {};
    patchThemeDom(s[ids.items], 'items');
    patchThemeDom(s[ids.footer], 'footer');
    var doc = new DOMParser().parseFromString(s[ids.items] || '', 'text/html');
    var data = parseCartData(doc);
    if(data){ buildItems(data); requestPlan(); }
    busy = false;
    if(done) done(true);
    render(true);
    document.dispatchEvent(new CustomEvent('pallet:cart-updated', {detail: cart}));
  })
  .catch(function(e){
    console.warn('[pallet] update failed', e);
    busy = false;
    var err = document.getElementById('cart-errors');
    if(err) err.textContent = 'Could not update the cart. Please try again.';
    if(done) done(false);
    render(false);
  });
}

/* 1) observe the theme's own cart requests (qty stepper, remove) */
(function(){
  var of = window.fetch;
  window.fetch = function(input, init){
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var ours = init && init.headers && (init.headers['X-Pallet-Panel'] || (init.headers.get && init.headers.get('X-Pallet-Panel')));
    var p = of.apply(this, arguments);
    if(!ours && /\/cart\/(change|update|add|clear)/.test(url)){
      p.then(function(){ scheduleReload(80); }).catch(function(){});
    }
    return p;
  };
})();

/* 2) fallback: theme re-rendered #main-cart-items (XHR, or fetch captured before us) */
(function(){
  var t = document.getElementById('main-cart-items');

  /* true when a mutation was caused by our own decorateRows() */
  function isOwn(mu){
    var tg = mu.target;
    if(tg && tg.nodeType === 3) tg = tg.parentNode;                  /* text node → element */
    if(tg && tg.closest && tg.closest('.cart-item-meta')) return true;
    if(mu.addedNodes.length && !mu.removedNodes.length){
      var allMeta = Array.prototype.every.call(mu.addedNodes, function(n){
        return n.classList && n.classList.contains('cart-item-meta');
      });
      if(allMeta) return true;
    }
    return false;
  }

  if(t && window.MutationObserver){
    new MutationObserver(function(muts){
      if(busy) return;                                               /* our own patchThemeDom */
      var changed = muts.some(function(mu){
        if(isOwn(mu)) return false;
        return mu.addedNodes.length || mu.removedNodes.length;
      });
      if(changed) scheduleReload(150);
    }).observe(t, {childList:true, subtree:true});
  }
  ['cart:updated','cart:refresh','cart-update'].forEach(function(ev){
    document.addEventListener(ev, function(){ if(!busy) scheduleReload(150); });
  });
})();

/* 3) The snippet's shipduo:rate / shipduo:rate:cleared events are no longer
      listened to here. Their only reader was the old empty-space banner's
      dollar figure, and the under-filled pallet notice that replaced it
      names cases and pallet counts, never a price — so this panel now has
      nothing to do with the freight amount at all. */

/* =====================================================================
   INTERACTIONS
   ===================================================================== */
function applySuggestion(i){
  if(busy) return;
  var x = lastList[i]; if(!x) return;
  var before = computeMetrics().freight, deltas = {}, updates = {};

  if(x.kind === 'add'){
    var it = items.find(function(y){ return y.sku === x.item.sku });
    if(!it) return;
    deltas[it.sku] = x.n;
    updates[it.id] = it.qty + x.n;
  } else {
    items.forEach(function(y){
      var cut = x.cuts[y.sku] || 0;
      if(!cut) return;
      var real = Math.min(cut, y.qty);
      deltas[y.sku] = -real;
      updates[y.id] = y.qty - real;
    });
  }

  var title = (x.kind === 'add'
    ? 'Added ' + x.n + ' × ' + x.item.name
    : 'Removed ' + x.n + ' case' + (x.n > 1 ? 's' : '') + ' — one pallet less');

  updateCart(updates, function(ok){
    if(!ok) return;
    var after = computeMetrics();
    appliedList.push({ deltas: deltas, title: title,
      detail: 'freight dropped <b>' + usd(Math.max(0, before - after.freight)) + '</b>' });
    flashSkus = Object.keys(deltas);
  });
}

function undo(i){
  if(busy) return;
  var act = appliedList[i]; if(!act) return;
  var updates = {};
  items.forEach(function(y){
    var d = act.deltas[y.sku];
    if(d) updates[y.id] = Math.max(0, y.qty - d);
  });
  Object.keys(act.deltas).forEach(function(vid){
    if(!items.some(function(y){ return y.sku === vid }) && act.deltas[vid] < 0){
      updates[vid] = -act.deltas[vid];
    }
  });
  updateCart(updates, function(ok){
    if(!ok) return;
    flashSkus = Object.keys(act.deltas);
    appliedList.splice(i, 1);
  });
}

function toggleRec(){ recClosed = !recClosed; var r=document.getElementById('rec'); if(r) r.classList.toggle('closed', recClosed); }

/* ── camera controls (called from Liquid buttons) ── */
function zoomBy(dir){
  userZoomed = true;
  zoomTo(clampRadius(radius + dir*55), null);
}
function resetView(){
  var cfg = (typeof lastCfg !== 'undefined' && lastCfg) ? lastCfg : RULES;
  theta = -0.7; phi = 1.46;
  target.set(0, (cfg.deck + cfg.maxH)/2, 0);
  userZoomed = false; framedOnce = false;
  frameStage(lastPallets, false);
}

/* destinationText() and its two listeners are gone: they read
   `.shipping-calculator select.country` / `input.postcode`, and that element
   was removed from main-cart-footer along with shipping-calculator.js. They
   wrote '' into #tDest on every keystroke, wiping the ShipDuo destination
   line. The snippet fills #tDest from its own #shipduo-country /
   #shipduo-postcode fields. */

/* =====================================================================
   BOOT
   ===================================================================== */
(function boot(){
  if(window.matchMedia && window.matchMedia('(pointer:coarse)').matches){
    var h=document.getElementById('hint');
    if(h) h.textContent = 'Two fingers to rotate and zoom · three to pan';
  }
  document.body.classList.add('light');
  buildItems(parseCartData(document));
  if(document.getElementById('canvasWrap') && window.THREE){
    initThree();
    sceneReady = true;
  }
  render(false);
  requestPlan();
})();